import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import MaterialItemSelector from '../MaterialItemSelector';

/**
 * CreateMRForm — Stores/Site raise a Material Requisition against the project's
 * Material Sheet (PMS) universe. Used on desktop (MR tab) and mobile.
 * Procurement never creates MRs (Tier-1 boundary).
 */
export function CreateMRForm({ projectId = '', onCreated, onCancel, compact = false }) {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(projectId || '');
  const [dept, setDept] = useState('Site Execution');
  const [requiredBy, setRequiredBy] = useState('');
  const [purpose, setPurpose] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  // S6 (30-Sep-2026 Stores doc): where the material comes from.
  const [sourceType, setSourceType] = useState('own_site');
  const [sourceProject, setSourceProject] = useState('');

  useEffect(() => {
    apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r?.items || r?.projects || []))).catch(() => {});
  }, []);
  useEffect(() => { if (projectId) setProject(projectId); }, [projectId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!project) return toast.error('Select a project');
    if (!items.length) return toast.error('Add at least one material line');
    if (sourceType === 'other_project' && !sourceProject) return toast.error('Select the source project');
    setSaving(true);
    try {
      const r = await apiPost('/stores/mr', {
        project_id: project,
        requested_by_department: dept || undefined,
        remarks: remarks || undefined,
        source_type: sourceType,
        source_project_id: sourceType === 'other_project' ? sourceProject : undefined,
        items: items.map(i => ({ boq_id: i.boq_id, requested_qty: i.quantity, required_by_date: requiredBy || undefined, purpose: purpose || undefined })),
      });
      toast.success(`${r.mr_no || 'MR'} raised — sent to PM for approval`);
      if (r.no_pm_warning) toast.warning(r.no_pm_warning);
      setItems([]); setRemarks(''); setPurpose(''); setSourceType('own_site'); setSourceProject('');
      onCreated?.(r);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not raise MR'));
    } finally { setSaving(false); }
  };

  const field = 'border border-slate-300 rounded-sm px-3 py-2 text-sm w-full focus:outline-none focus:border-[#3b4872] bg-white';
  return (
    <form onSubmit={submit} className={`space-y-3 ${compact ? '' : 'bg-white border border-slate-200 rounded-md p-4'}`} data-testid="create-mr-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-slate-800">New Material Requisition</h3>
        {onCancel && <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="create-mr-cancel"><X size={16} /></button>}
      </div>
      <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'}`}>
        <label className="text-xs text-slate-600">Project
          <select value={project} onChange={e => { setProject(e.target.value); setItems([]); }} className={field} data-testid="create-mr-project">
            <option value="">Select project…</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.code}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600">Department
          <Input value={dept} onChange={e => setDept(e.target.value)} placeholder="Site Execution" data-testid="create-mr-dept" />
        </label>
        <label className="text-xs text-slate-600">Required by
          <Input type="date" value={requiredBy} onChange={e => setRequiredBy(e.target.value)} data-testid="create-mr-required-by" />
        </label>
        <label className="text-xs text-slate-600">Purpose / activity
          <Input value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="e.g. Footing F3 casting" data-testid="create-mr-purpose" />
        </label>
        <label className="text-xs text-slate-600">Source
          <select value={sourceType} onChange={e => { setSourceType(e.target.value); setSourceProject(''); setItems([]); }} className={field} data-testid="create-mr-source-type">
            <option value="own_site">Own Site Store</option>
            <option value="other_project">Another Project's Store</option>
            <option value="godown">Central Godown</option>
          </select>
        </label>
        {sourceType === 'other_project' && (
          <label className="text-xs text-slate-600">Source Project
            <select value={sourceProject} onChange={e => setSourceProject(e.target.value)} className={field} data-testid="create-mr-source-project">
              <option value="">Select project…</option>
              {projects.filter(p => p.id !== project).map(p => <option key={p.id} value={p.id}>{p.name || p.code}</option>)}
            </select>
          </label>
        )}
      </div>
      <MaterialItemSelector
        projectId={project}
        mode="mr"
        selectedItems={items}
        onItemsChange={setItems}
        sourceType={sourceType}
        sourceProjectId={sourceType === 'other_project' ? sourceProject : undefined}
      />
      <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks (optional)" data-testid="create-mr-remarks" />
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>}
        <Button type="submit" disabled={saving} className="bg-[#3b4872] hover:bg-[#2d3856]" data-testid="create-mr-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Raise MR
        </Button>
      </div>
    </form>
  );
}

export default CreateMRForm;
